import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';
import SwiperCore, { EffectCoverflow, Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';

import { RelocationInterestFormModal } from './RelocationInterestForm';

import { LinkButton } from '@/src/components/Button';
import {
  SectionTitleComponent,
  SectionLabel,
  OverviewDescriptionComponent,
  ImageWrapper,
} from '@/src/components/OverviewSection/OverviewSectionRow';
import { WhyRemoteLabel, TitleComponent, DescriptionComponent } from '@/src/components/WhyRemote';
import { fieldHasContent } from '@/src/domains/prismic/helpers';
import { defaultPrismicComponents } from '@/src/domains/prismic/render';
import {
  tabletBreakpoint,
  desktopBreakpoint,
  mobileBreakpoint,
} from '@/src/domains/shared/breakpoints';
import { AnimatedSectionWrapper } from '@/src/style/motion';

import 'swiper/swiper.min.css';
import 'swiper/components/effect-coverflow/effect-coverflow.min.css';

SwiperCore.use([EffectCoverflow, Autoplay]);

export const SectionWrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '130px')}
  background-image: var(--colors-zircon);
`;

const OverviewRowWrapper = styled.div`
  ${fluidProp(['padding-bottom', 'padding-top'], '70px', '120px')}
  display: flex;
  justify-content: space-between;

  ${desktopBreakpoint(css`
    justify-content: space-around;
  `)}

  ${tabletBreakpoint(css`
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-bottom: 0;
  `)}

  ul {
    columns: 2;

    ${mobileBreakpoint(css`
      columns: 1;
    `)}
  }
`;

export const ReverseOverviewRowWrapper = styled(OverviewRowWrapper)`
  flex-direction: row-reverse;

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}
`;

export const OverviewRowContent = styled.div`
  max-width: 45%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${tabletBreakpoint(css`
    max-width: 75%;
    margin-bottom: 90px;
  `)}
`;

export const CardEntry = styled.div`
  width: 299px;
  height: 290px;
  background-color: var(--colors-blank);
  box-shadow: 1px 8px 45px rgba(0, 35, 75, 0.09);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const CardTitle = styled.div`
  ${({ theme }) => theme.typography.scale2}
  color: var(--colors-irisBlue);
  ${fluidProp('font-size', '38px', '64px')}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const CardsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 29px;
`;

export const CardDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-bayoux);
  max-width: 194px;
  margin: 10px auto 0;
  text-align: center;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const CTAButton = styled(LinkButton)`
  && {
    max-width: 170px;
    margin: 22px 0;
  }
`;

function updateClasses({ $el, slides, activeIndex }) {
  $el.find('.swiper-slide-prev-prev').removeClass('swiper-slide-prev-prev');
  slides.eq(activeIndex).prev().prev().addClass('swiper-slide-prev-prev');

  $el.find('.swiper-slide-next-next').removeClass('swiper-slide-next-next');
  slides.eq(activeIndex).next().next().addClass('swiper-slide-next-next');
}

export const FlagsContainer = styled.div`
  justify-self: center;

  .swiper-container {
    width: 218px;
    max-height: 470px;
    padding-top: 1px;
    margin-bottom: 30px;
  }

  .swiper-slide {
    background-position: center;
    background-size: cover;
    width: 218px;
    height: 142px;
    border-radius: 20px;
    overflow: hidden;
    opacity: 0;
  }

  .swiper-slide img {
    display: block;
    width: 100%;
  }

  .swiper-slide-active,
  .swiper-slide-prev,
  .swiper-slide-next {
    opacity: 1;
  }

  .swiper-slide-prev-prev,
  .swiper-slide-next-next {
    opacity: 0.4;
  }
`;

export const FlagsSwiper = styled(Swiper)`
  .swiper-container {
    width: 218px;
    max-height: 470px;
    padding-top: 1px;
    margin-bottom: 30px;
  }

  .swiper-slide {
    background-position: center;
    background-size: cover;
    width: 218px;
    height: 142px;
    border-radius: 20px;
    overflow: hidden;
    opacity: 0;
  }

  .swiper-slide img {
    display: block;
    width: 100%;
  }

  .swiper-slide-active,
  .swiper-slide-prev,
  .swiper-slide-next {
    opacity: 1;
  }

  .swiper-slide-prev-prev,
  .swiper-slide-next-next {
    opacity: 0.4;
  }
`;

const CountryFlagSlider = ({ flags }) => (
  <FlagsContainer>
    <FlagsSwiper
      onInit={(el, slides, activeIndex) => {
        updateClasses(el, slides, activeIndex);
      }}
      onSlideChange={(el, slides, activeIndex) => {
        updateClasses(el, slides, activeIndex);
      }}
      effect="coverflow"
      grabCursor
      centeredSlides
      slidesPerView="auto"
      loop
      autoplay
      loopedSlides={5}
      coverflowEffect={{
        rotate: 10,
        stretch: 50,
        depth: 180,
        modifier: 1,
        slideShadows: true,
      }}
      direction="vertical"
    >
      {flags.map((flag, index) => (
        <SwiperSlide key={index}>
          <img
            src={flag.flag_image?.url}
            alt={`${flag.flag_image?.alt} flag`}
            width="218"
            height="142"
          />
        </SwiperSlide>
      ))}
    </FlagsSwiper>
  </FlagsContainer>
);

export function Entry({ title, description }) {
  return (
    <AnimatedSection ComponentName={CardEntry}>
      {title && <RichText render={title} Component={CardTitle} />}
      {description && <RichText render={description} Component={CardDescription} />}
    </AnimatedSection>
  );
}

export function WhyRemoteSection({
  label,
  title,
  description,
  list = [],
  flagLabel,
  flagTitle,
  flagDescription,
  globeSection,
  hubspotFormDefinition,
  form,
  flags,
  ...props
}) {
  const {
    label: globeLabel,
    title: globeTitle,
    description: globeDescription,
    illustration,
    cta_label: ctaLabel,
    cta_bottom_text: ctaBottomText,
  } = globeSection[0];
  const [displayGatedModal, setDisplayGatedModal] = useState(false);

  function openGatedModal() {
    setDisplayGatedModal(true);
  }
  function closeGatedModal() {
    setDisplayGatedModal(false);
  }

  return (
    <SectionWrapper {...props}>
      <ContentContainer>
        <AnimatedSection ComponentName={AnimatedSectionWrapper}>
          {label && (
            <WhyRemoteLabel>
              <small>{label}</small>
            </WhyRemoteLabel>
          )}
          {title && <RichText render={title} Component={TitleComponent} />}
          {description && <RichText render={description} Component={DescriptionComponent} />}
        </AnimatedSection>
        <CardsList>
          {list.map((listItem, index) => (
            <Entry {...listItem} key={index} />
          ))}
        </CardsList>

        <OverviewRowWrapper>
          <OverviewRowContent>
            {flagLabel && <SectionLabel>{flagLabel}</SectionLabel>}
            {flagTitle && <RichText render={flagTitle} Component={SectionTitleComponent} />}
            {flagDescription && (
              <RichText render={flagDescription} Component={OverviewDescriptionComponent} />
            )}
          </OverviewRowContent>
          <CountryFlagSlider flags={flags} />
        </OverviewRowWrapper>
        <ReverseOverviewRowWrapper>
          <OverviewRowContent>
            {globeLabel && <SectionLabel>{globeLabel}</SectionLabel>}
            {globeTitle && <RichText render={globeTitle} Component={SectionTitleComponent} />}
            {globeDescription && (
              <RichText render={globeDescription} Component={OverviewDescriptionComponent} />
            )}
            <CTAButton
              withBackground
              irisBlueBG
              label={ctaLabel ?? "Let's talk"}
              onClick={openGatedModal}
            />

            <RelocationInterestFormModal
              form={form}
              hubspotFormDefinition={hubspotFormDefinition}
              visible={displayGatedModal}
              onCancel={closeGatedModal}
            />

            {fieldHasContent(ctaBottomText) && (
              <OverviewDescriptionComponent>
                <PrismicRichText
                  field={ctaBottomText}
                  Component={OverviewDescriptionComponent}
                  components={defaultPrismicComponents}
                />
              </OverviewDescriptionComponent>
            )}
          </OverviewRowContent>
          <ImageWrapper>
            {illustration && (
              <Image
                src={illustration.url}
                alt={illustration.alt || ''}
                width={illustration.dimensions.width}
                height={illustration.dimensions.height}
                quality="100"
              />
            )}
          </ImageWrapper>
        </ReverseOverviewRowWrapper>
      </ContentContainer>
    </SectionWrapper>
  );
}
